/**
 * 模組代碼 : -
 * 模組說明 : (共用模組) 匯入檔案內容
 * @module web/components/ImportData
 */

// ----- import from npm modules -----
import React, { useState, useRef, useEffect } from 'react';
import Papa from 'papaparse';

const ImportDataModal = React.forwardRef((props, ref) => {
    const { onDataHandle } = props;
    const [selectedFile, setSelectedFile] = useState(null);

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const ALLOWED_MIME_TYPES = ['text/csv', 'application/vnd.ms-excel', 'text/plain', ''];

    // 已知惡意檔案 magic bytes（防止改附檔名繞過）
    const BINARY_MAGIC_BYTES = [
        { name: 'EXE/DLL',   bytes: [0x4D, 0x5A] },                          // MZ
        { name: 'ELF',       bytes: [0x7F, 0x45, 0x4C, 0x46] },              // .ELF
        { name: 'ZIP/DOCX',  bytes: [0x50, 0x4B, 0x03, 0x04] },              // PK..
        { name: 'RAR',       bytes: [0x52, 0x61, 0x72, 0x21] },              // Rar!
        { name: 'PDF',       bytes: [0x25, 0x50, 0x44, 0x46] },              // %PDF
        { name: 'PNG',       bytes: [0x89, 0x50, 0x4E, 0x47] },              // .PNG
        { name: 'JPEG',      bytes: [0xFF, 0xD8, 0xFF] },                    // JPEG SOI
        { name: 'GIF',       bytes: [0x47, 0x49, 0x46, 0x38] },              // GIF8
        { name: 'Class',     bytes: [0xCA, 0xFE, 0xBA, 0xBE] },              // Java
        { name: 'Mach-O',    bytes: [0xFE, 0xED, 0xFA, 0xCE] },              // Mach-O
        { name: '7z',        bytes: [0x37, 0x7A, 0xBC, 0xAF, 0x27, 0x1C] },
    ];

    // 同步基礎驗證（檔名/副檔名/MIME/大小）
    const validateFile = (file) => {
        if (!file) return '請選擇檔案。';
        if (!/\.csv$/i.test(file.name)) return '僅接受 .csv 副檔名之檔案。';
        if (!ALLOWED_MIME_TYPES.includes(file.type)) return '檔案類型不符，僅接受 CSV。';
        if (file.size > MAX_FILE_SIZE) return '檔案大小不可超過 5MB。';
        return null;
    };

    // 非同步內容驗證：讀前 512 bytes 檢查 magic bytes 與是否為純文字
    const validateFileContent = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const buffer = new Uint8Array(e.target.result);

                // 1. Magic bytes 比對：開頭是已知二進位格式 → 拒絕
                for (const sig of BINARY_MAGIC_BYTES) {
                    const match = sig.bytes.every((b, i) => buffer[i] === b);
                    if (match) {
                        resolve(`檔案內容為 ${sig.name} 格式，非合法 CSV。`);
                        return;
                    }
                }

                // 2. 不可列印字元比例 > 10% → 視為二進位檔
                let nonPrintable = 0;
                const sampleLen = Math.min(buffer.length, 512);
                for (let i = 0; i < sampleLen; i++) {
                    const b = buffer[i];
                    // 允許：可列印 ASCII (0x20~0x7E)、Tab、CR、LF、UTF-8 高位元組 (>= 0x80)
                    const printable = (b >= 0x20 && b <= 0x7E) || b === 0x09 || b === 0x0A || b === 0x0D || b >= 0x80;
                    if (!printable) nonPrintable++;
                }
                if (sampleLen > 0 && nonPrintable / sampleLen > 0.1) {
                    resolve('檔案內容含過多二進位資料，非合法 CSV。');
                    return;
                }

                resolve(null);
            };
            reader.onerror = () => resolve('讀取檔案失敗。');
            reader.readAsArrayBuffer(file.slice(0, 512));
        });
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        const basicError = validateFile(file);
        if (basicError) {
            alert(basicError);
            event.target.value = '';
            setSelectedFile(null);
            return;
        }
        const contentError = await validateFileContent(file);
        if (contentError) {
            alert(contentError);
            event.target.value = '';
            setSelectedFile(null);
            return;
        }
        setSelectedFile(file);
    };

    const handleFileImport = async (event) => {
        event.preventDefault();
        const basicError = validateFile(selectedFile);
        if (basicError) {
            alert(basicError);
            return;
        }
        const contentError = await validateFileContent(selectedFile);
        if (contentError) {
            alert(contentError);
            return;
        }
        Papa.parse(selectedFile, {
            complete: function(results) {
                // 內容驗證：若解析出現致命錯誤，視為非合法 CSV
                if (results.errors && results.errors.length > 0) {
                    const fatal = results.errors.find(e => e.type === 'Delimiter' || e.type === 'Quotes');
                    if (fatal) {
                        alert('檔案內容非合法 CSV 格式，請確認後重新選擇。');
                        return;
                    }
                }
                // 內容驗證：至少要有一列資料
                if (!results.data || results.data.length === 0) {
                    alert('CSV 檔案為空或無可讀取資料。');
                    return;
                }
                onDataHandle(results.data);
            },
            error: function() {
                alert('檔案解析失敗，請確認為合法 CSV 檔案。');
            },
            header: true, // Assumes the CSV file has headers
            skipEmptyLines: true,
        });
    };

    useEffect(() => {
    }, []);

    return (
        <div ref={ref}
            className="modal fade"
            id="modalImport"
            tabIndex="-1"
            aria-labelledby="modalImportLabel"
            aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <form className="modal__form modal-content" id="modalImportForm">
                    <div className="modal-header border-bottom-0">
                        <h1 className="modal-title fs-5" id="modalImportLabel">
                            <div className="modal__title__badge" id="modalImportBadge">
                                <svg className="badge__icon" aria-hidden="true">
                                    <use href="#ico-download"></use>
                                </svg>
                            </div>
                            <div className="modal__title__text">匯入資料</div>
                        </h1>
                        <button type="button" className="btn-close btn-cancel" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="d-flex flex-column gap-3 px-2">
                            <div className="row">
                                <div className="col">
                                    <input className="form-control" type="file" id="modalImportFile" accept=".csv,text/csv" onChange={handleFileChange} />
                                    <label htmlFor="modalImportFile" className="form-label">
                                        您的 CSV 檔案必須使用正確的格式，且必須有「.csv」檔案副檔名。
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-cancel" data-bs-dismiss="modal">取消</button>
                        <button type="submit" className="btn btn-primary" onClick={handleFileImport}>匯入</button>
                    </div>
                </form>
            </div>
        </div>
    );
});

export { ImportDataModal };